import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';
import { ItineraryDetailsModal } from '@/lib/itineraries/ItineraryDetailsModal';
import { ItineraryNotesModal } from '@/lib/itineraries/ItineraryNotesModal';
import { SendQuoteModal } from '@/lib/itineraries/SendQuoteModal';
import { CreateQuoteModal } from '@/lib/itineraries/CreateQuoteModal';
import { ImagePreviewModal } from '@/components/ui/ImagePreviewModal';
import { MediaCarouselModal } from '@/components/ui/MediaCarouselModal';
import { Itinerary } from '@/hooks/useItineraries';

type ModalType = 'itinerary-details' | 'notes' | 'send-quote' | 'create-quote' | 'image-preview' | 'media-carousel';

interface ModalConfig {
  type: ModalType;
  props?: any;
}

interface ModalContextType {
  openModal: (type: ModalType, props?: any) => void;
  closeModal: () => void;
  isOpen: boolean;
  currentModal: ModalConfig | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

// How long to keep the component mounted after isOpen → false (must match Modal transition duration)
const CLOSE_ANIMATION_MS = 220;

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  // currentModal drives isOpen — set to null immediately on close
  const [currentModal, setCurrentModal] = useState<ModalConfig | null>(null);
  // displayedModal keeps the component mounted during the exit animation
  const [displayedModal, setDisplayedModal] = useState<ModalConfig | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openModal = useCallback((type: ModalType, props?: any) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    const config = { type, props };
    setCurrentModal(config);
    setDisplayedModal(config);
  }, []);

  const closeModal = useCallback(() => {
    setCurrentModal(null); // triggers isOpen=false → exit animation starts
    closeTimerRef.current = setTimeout(() => setDisplayedModal(null), CLOSE_ANIMATION_MS);
  }, []);

  const isOpen = currentModal !== null;

  return (
    <ModalContext.Provider value={{ openModal, closeModal, isOpen, currentModal }}>
      {children}

      {/* displayedModal keeps the component alive during the exit animation.
          isOpen is derived from currentModal so the Modal receives false while still mounted. */}
      {displayedModal?.type === 'itinerary-details' && (
        <ItineraryDetailsModal
          isOpen={currentModal?.type === 'itinerary-details'}
          onClose={closeModal}
          itinerary={displayedModal.props?.itinerary}
          onViewItinerary={displayedModal.props?.onViewItinerary}
          showButton={displayedModal.props?.showButton}
          durationDays={displayedModal.props?.durationDays}
          onLinkToRequest={displayedModal.props?.onLinkToRequest}
        />
      )}

      {displayedModal?.type === 'notes' && (
        <ItineraryNotesModal
          isOpen={currentModal?.type === 'notes'}
          onClose={closeModal}
          note={displayedModal.props?.note}
        />
      )}

      {displayedModal?.type === 'send-quote' && (
        <SendQuoteModal
          isOpen={currentModal?.type === 'send-quote'}
          onClose={closeModal}
          itinerary={displayedModal.props?.itinerary}
          onSuccessClose={displayedModal.props?.onSuccessClose}
        />
      )}

      {displayedModal?.type === 'create-quote' && (
        <CreateQuoteModal
          isOpen={currentModal?.type === 'create-quote'}
          onClose={closeModal}
          onSend={displayedModal.props?.onSend}
        />
      )}

      {displayedModal?.type === 'image-preview' && (
        <ImagePreviewModal
          isOpen={currentModal?.type === 'image-preview'}
          onClose={closeModal}
          src={displayedModal.props?.src}
        />
      )}

      {displayedModal?.type === 'media-carousel' && (
        <MediaCarouselModal
          isOpen={currentModal?.type === 'media-carousel'}
          onClose={closeModal}
          media={displayedModal.props?.media}
          initialIndex={displayedModal.props?.initialIndex}
        />
      )}
    </ModalContext.Provider>
  );
};
